import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { PasswordInput } from '@ui/password-input.tsx';
import { Button } from '@ui/button.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import { Checkbox } from '@ui/checkbox.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { ComboboxInput } from '@ui/combobox-input.tsx';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import type { Option } from '@r/types/index.ts';
import { PASSWORD_REGEX } from '@r/utils/regex/password.ts';
import { useNavigate } from '@tanstack/react-router';
import { type FC, useEffect, useState } from 'react';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { getAssignableRoles, roleToI18nKey } from '@r/utils/roles.ts';
import {
  useApiAdmincompaniesGetCollection,
  useApiAdminusersPost,
  getApiAdminusersIdGetQueryKey,
  getApiAdminusersGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type {
  CompanyCompanyCollectionReadModel,
  UserUserImportModel,
  UserWorkspaceUserImportModelMaxRole
} from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useQueryClient } from '@tanstack/react-query';

interface CreateUserFormProps {
  onSuccess: () => void;
  initialCompanyOption?: Option | null;
}

const COMMENT_MAX_LENGTH = 255;

export const CreateUserForm: FC<CreateUserFormProps> = ({ onSuccess, initialCompanyOption }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useUser();
  const activeWS = getActiveWorkspaceUniversal(currentUser.data?.visibleWorkspaces ?? []);
  const assignableRoles = getAssignableRoles(activeWS?.maxRole);
  const isCompanyPrefilled = Boolean(initialCompanyOption?.value);
  const showOrvalErrorToast = useOrvalErrorToast();
  const availableRoles = isCompanyPrefilled
    ? assignableRoles.filter((role) => role === 'ROLE_MEMBER' || role === 'ROLE_MANAGER')
    : assignableRoles;
  const queryClient = useQueryClient();

  const createUserSchema = z.object({
    email: z
      .string()
      .email(t('user.create.email.invalid'))
      .min(1, t('user.create.email.required'))
      .describe(t('personal.information.schema.describe.email')),
    password: z.string().regex(PASSWORD_REGEX, {
      message: t('user.create.password.invalid')
    }),
    firstname: z
      .string()
      .min(1, t('user.create.first.name.required'))
      .min(2, t('user.create.first.name.min'))
      .describe(t('personal.information.schema.describe.firstname')),
    lastname: z
      .string()
      .min(1, t('user.create.last.name.required'))
      .min(2, t('user.create.last.name.min'))
      .describe(t('personal.information.schema.describe.lastname')),
    clientCode: z
      .string()
      .min(1, t('user.create.client.code.required'))
      .min(3, t('user.create.client.code.min')),
    phone: z.string().optional().describe(t('personal.information.schema.describe.phone')),
    comment: z
      .string()
      .max(
        COMMENT_MAX_LENGTH,
        t('admin.user.profile.validation.comment.max.length', { max: COMMENT_MAX_LENGTH })
      )
      .optional(),
    comment2: z
      .string()
      .max(
        COMMENT_MAX_LENGTH,
        t('admin.user.profile.validation.comment.max.length', { max: COMMENT_MAX_LENGTH })
      )
      .optional(),
    comment3: z
      .string()
      .max(
        COMMENT_MAX_LENGTH,
        t('admin.user.profile.validation.comment.max.length', { max: COMMENT_MAX_LENGTH })
      )
      .optional(),
    role: z.enum(
      ['ROLE_MEMBER', 'ROLE_MANAGER', 'ROLE_SALES_ADMIN', 'ROLE_ADMIN', 'ROLE_FIDDY_ADMIN'],
      {
        required_error: t('user.create.role.required')
      }
    ),
    company: z.string().optional(),
    emailNotification: z.boolean().optional()
  });

  const form = useForm<z.infer<typeof createUserSchema>>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      password: '',
      firstname: '',
      lastname: '',
      clientCode: '',
      phone: '',
      comment: '',
      comment2: '',
      comment3: '',
      role: undefined,
      company: '',
      emailNotification: false
    }
  });

  const [companyQuery, setCompanyQuery] = useState('');
  const debouncedCompanyQuery = useDebounce(companyQuery, 500);
  const [selectedCompanyOption, setSelectedCompanyOption] = useState<Option | null>(null);

  useEffect(() => {
    if (!initialCompanyOption) return;
    setSelectedCompanyOption(initialCompanyOption);
    setCompanyQuery(initialCompanyOption.label);
    form.setValue('company', initialCompanyOption.value);
  }, [initialCompanyOption, form]);

  const companiesQuery = useApiAdmincompaniesGetCollection(
    debouncedCompanyQuery && debouncedCompanyQuery.length > 0
      ? { search: debouncedCompanyQuery, perPage: 50 }
      : { 'order[name]': 'asc' as const }
  );

  const companyOptions: Option[] = (companiesQuery.data?.data ?? []).map(
    (company: CompanyCompanyCollectionReadModel) => ({
      label: `${company.name}`,
      value: company.uniqueCode
    })
  );

  const createUserMutation = useApiAdminusersPost({
    mutation: {
      onSuccess: (data) => {
        if (data?.id) {
          queryClient.setQueryData(getApiAdminusersIdGetQueryKey(String(data.id)), data);
        }
        queryClient.invalidateQueries({ queryKey: getApiAdminusersGetCollectionQueryKey() });
        toast.success(t('user.create.success'));
        form.reset();
        onSuccess();
        navigate({ to: `/users/${data.id}` });
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('user.create.error'));
      }
    }
  });

  function onSubmit(data: z.infer<typeof createUserSchema>) {
    if (!availableRoles.includes(data.role)) {
      form.setError('role', {
        type: 'manual',
        message: t('common.not.allowed')
      });
      toast.error(t('common.not.allowed'));
      return;
    }

    if (
      (data.role === 'ROLE_MANAGER' || data.role === 'ROLE_MEMBER') &&
      (!data.company || data.company.trim().length === 0)
    ) {
      form.setError('company', {
        type: 'manual',
        message: t('user.create.company.required')
      });
      return;
    }

    const payload: UserUserImportModel = {
      email: data.email,
      plainPassword: data.password,
      firstname: data.firstname,
      lastname: data.lastname,
      userWorkspace: {
        maxRole: data.role as UserWorkspaceUserImportModelMaxRole,
        companyUniqueCode: data.company && data.company.trim().length > 0 ? data.company : null,
        uniqueCode: data.clientCode,
        phone: data.phone,
        comment: data.comment && data.comment.trim().length > 0 ? data.comment.trim() : null,
        comment2: data.comment2 && data.comment2.trim().length > 0 ? data.comment2.trim() : null,
        comment3: data.comment3 && data.comment3.trim().length > 0 ? data.comment3.trim() : null
      },
      emailNotification: data.emailNotification ?? false
    };
    createUserMutation.mutate({ data: payload });
  }

  return (
    <Form {...form} schema={createUserSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('user.create.email.label')}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t('user.create.email.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('user.create.password.label')}</FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder={t('user.create.password.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstname"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('user.create.first.name.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('user.create.first.name.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastname"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('user.create.last.name.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('user.create.last.name.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="clientCode"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('user.create.client.code.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('user.create.client.code.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('user.create.phone.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('user.create.phone.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="comment"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('label.user.comment')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('user.comment.placeholder')}
                  {...field}
                  value={field.value ?? ''}
                  maxLength={COMMENT_MAX_LENGTH}
                  rows={4}
                  className="resize-none"
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="comment2"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('label.user.comment2')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('user.comment.placeholder')}
                  {...field}
                  value={field.value ?? ''}
                  maxLength={COMMENT_MAX_LENGTH}
                  rows={4}
                  className="resize-none"
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="comment3"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('label.user.comment3')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('user.comment.placeholder')}
                  {...field}
                  value={field.value ?? ''}
                  maxLength={COMMENT_MAX_LENGTH}
                  rows={4}
                  className="resize-none"
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('user.create.role.label')}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('user.create.role.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {t(roleToI18nKey(role))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* recherche de sociétés */}
        <FormField
          control={form.control}
          name="company"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('user.create.company.label')}</FormLabel>
              <FormControl>
                <ComboboxInput
                  placeholder={t('user.create.company.placeholder')}
                  immediate={true}
                  options={companyOptions}
                  input={companyQuery}
                  onInputChange={setCompanyQuery}
                  value={selectedCompanyOption}
                  onValueChange={(option) => {
                    setSelectedCompanyOption(option);
                    field.onChange(option?.value ?? '');
                  }}
                  loading={companiesQuery.isLoading}
                  className={fieldState.error ? 'border-destructive' : undefined}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {form.watch('role') === 'ROLE_MANAGER' || form.watch('role') === 'ROLE_MEMBER' ? (
          <FormField
            control={form.control}
            name="emailNotification"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>{t('user.create.email.notification.label')}</FormLabel>
                </div>
              </FormItem>
            )}
          />
        ) : null}

        <Button
          rounded="full"
          loading={createUserMutation.isPending}
          className="w-full"
          type="submit"
        >
          <Plus className="mr-2 w-4 h-4" />
          {t('user.create.form.button')}
        </Button>
      </form>
    </Form>
  );
};
